/**
 * What a page about ONE node or network does when the store does not hold it (app#473).
 *
 * Pure, so `missingObject.test.ts` can check it with `node --experimental-strip-types`.
 */

/**
 * The answer of the direct read of the object: the id of the organization it belongs to; `false`
 * when the reader can read no such object (it is removed, it never was, or it is in an
 * organization the reader is not in); `null` when the read failed. `undefined`: not asked yet.
 */
export type Asked = string | false | null | undefined

export type MissingStep = 'wait' | 'ask' | 'switch' | 'close'

/**
 * - `wait`: draw what the page draws today, and do nothing.
 * - `ask`: read the object itself, once.
 * - `switch`: make the object's organization the active one. The page then draws the object.
 * - `close`: leave the page, for the org's home view.
 *
 * ⚠⚠ A PATH THAT NAMES AN OBJECT SAYS WHAT THE READER WANTS TO SEE (Benoit, 2026-10-07). When the
 * object is in ANOTHER organization of the reader, the app goes to that organization: the link of
 * an access-request mail, a bookmark, and Back to a page of the organization the reader left all
 * open what they name. The support incident of 2026-10-07 started with a mail link that opened
 * nothing (app#478). When the reader can read no such object, the page goes to the home view.
 *
 * ⚠⚠ THE STORE NOT HOLDING THE OBJECT IS NOT PROOF OF ANYTHING. Three cases look the same:
 *   1. The list is not read yet, or it is still the list of the organization the reader left
 *      (`loadedOrgId` is not the active organization). A reload of a node page is here for a
 *      moment, and the page must not act on a node that is about to arrive.
 *   2. The list is read and leaves the object out on purpose: the node list hides a node that
 *      went offline long ago (`hideOffline`), and a node that was just registered can arrive after
 *      its page opens. The read then names the ACTIVE organization: wait.
 *   3. The object is not an object of the active organization.
 * Only a direct read of the object tells 2 from 3. So the page asks once. A read that failed
 * (`null`) changes nothing.
 *
 * ⚠ `memberOrgIds` is the reader's own list. The read can only bring a row of one of them (RLS),
 * and the test is here too: the app never makes active an organization it does not list.
 */
export function missingStep(
  found: boolean,
  loadedOrgId: string | null | undefined,
  activeOrgId: string | null,
  asked: Asked,
  memberOrgIds: ReadonlyArray<string> = []
): MissingStep {
  if (found) return 'wait'
  if (!activeOrgId || loadedOrgId !== activeOrgId) return 'wait'
  if (asked === undefined) return 'ask'
  if (asked === null || asked === activeOrgId) return 'wait'
  if (asked !== false && memberOrgIds.includes(asked)) return 'switch'
  return 'close'
}
